import { useCallback, useEffect, useState } from "react";

/**
 * The session, and the export targets the server computed for it.
 *
 * WHY THE TARGET LIST IS COMPUTED ON THE SERVER, AND WHAT CACHING IT HERE DOES
 * AND DOES NOT BUY.
 *
 * A target is "a site this workspace may be asked to deploy a model to". Working
 * that out means holding credentials up to another company's service, deciding
 * whether the answer was an owner or a sub-user, remembering the answer for a
 * bounded time and throttling how often anyone may ask. None of that can happen
 * in a browser: the credentials would have to be here, the memory would be
 * per-device, and the throttle would be advice rather than a limit. So the list
 * arrives already decided, as `data.targets` on a login or a `/services/*` call.
 *
 * Keeping a copy in localStorage buys exactly one thing: the export screen can
 * draw the list on first paint without asking the user for their password again
 * (every `/services/*` route requires the actor's own password, by design — a
 * user id out of storage proves nothing).
 *
 * It buys NO authority whatever. GPORTAL's link code and a Gemmiz
 * administrator's approval are the only things that permit an export, and they
 * are checked again, there, on every request. So a cached target is a HINT ABOUT
 * WHAT TO SHOW. A stale or wrong one costs a clear error message from the
 * authority that refused it; it can never cause a deployment that was not
 * approved. That is why this module is allowed to be optimistic — and why it is
 * never allowed to be optimistic in the other direction: `writeTargets` is only
 * ever fed a server payload, and EVERY read degrades to an EMPTY list. A missing
 * key, unparseable JSON, a version that is not ours or a payload that is not an
 * array all read as "no targets", never as "all targets". Anything else would
 * let a corrupt cache invent a site to offer.
 */

/**
 * The exact keys. `userId` and `name` keep the names three existing screens and
 * `src/utils/auth.js` already read; the new ones are prefixed so they cannot
 * collide with the old build's leftovers.
 */
export const STORAGE_KEYS = {
  userId: "userId",
  name: "name",
  primaryProvider: "lf-primary-provider",
  targets: "lf-targets",
  providers: "lf-providers",
};

/**
 * The cache envelope version. The KEY NAMES NEVER CHANGE — the version lives
 * inside the value, so a build that writes a different shape is rejected by the
 * reader rather than shadowed by a second key nobody clears.
 */
export const TARGETS_VERSION = 1;

/**
 * What the previous build wrote and nothing ever read: the PLAINTEXT password,
 * the email, the country, and a surveillance-shaped `cameraInfo` array. Measured
 * across all of `src/`: zero readers each. They are removed on every session
 * write and on sign-out, so a browser that has signed in once since this change
 * no longer holds a live credential for another company's camera system.
 */
const LEGACY_KEYS = ["password", "email", "cCountry", "cameraInfo"];

/**
 * Same-tab notification. The `storage` event only fires in OTHER tabs, so
 * without this a connect or a disconnect would update the store and leave the
 * list on screen unchanged until a navigation. Same idiom as `src/i18n`.
 */
const CHANGE_EVENT = "lf-targets-changed";

const store = () => {
  if (typeof window === "undefined") return null;
  return window.localStorage;
};

const readRaw = (key) => {
  try {
    const ls = store();
    return ls ? ls.getItem(key) : null;
  } catch (error) {
    // Private windows and blocked site data make the accessor itself throw.
    console.warn(`Unable to read ${key} from storage`, error);
    return null;
  }
};

const writeRaw = (key, value) => {
  try {
    const ls = store();
    if (!ls) return false;
    ls.setItem(key, value);
    return true;
  } catch (error) {
    // A private window, blocked site data, or a full quota.
    console.warn(`Unable to write ${key} to storage`, error);
    return false;
  }
};

const removeRaw = (key) => {
  try {
    const ls = store();
    if (ls) ls.removeItem(key);
  } catch (error) {
    console.warn(`Unable to clear ${key} from storage`, error);
  }
};

const announce = () => {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(CHANGE_EVENT));
    }
  } catch (error) {
    console.warn("Unable to announce a target change", error);
  }
};

/**
 * Read one versioned list envelope. This is the function that has to fail
 * closed: every path out of it that is not a real, current, array-shaped payload
 * returns `[]`.
 */
const readList = (key, field) => {
  const raw = readRaw(key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return [];
    if (parsed.v !== TARGETS_VERSION) return [];
    const list = parsed[field];
    return Array.isArray(list) ? list : [];
  } catch (error) {
    console.warn(`Ignoring an unreadable ${key} cache`, error);
    return [];
  }
};

const writeList = (key, field, list) => {
  // A non-array caller is a bug upstream; storing `[]` keeps the invariant that
  // whatever comes back out of here is a list.
  const value = Array.isArray(list) ? list : [];
  const stored = writeRaw(key, JSON.stringify({ v: TARGETS_VERSION, [field]: value }));

  // A FAILED WRITE MUST LEAVE NOTHING BEHIND. `setItem` can refuse (a full
  // quota, blocked site data) after the session keys beside it were written —
  // and what is left in that case is the PREVIOUS answer: the last user's sites,
  // or a list that was just disconnected, now read back as this session's. The
  // reader fails closed on an absent key, so clearing it is the way to make a
  // failed write fail closed too. Best effort by definition: if the remove is
  // refused as well, storage is unusable and every read already returns `[]`.
  if (!stored) removeRaw(key);
};

/**
 * Persist a signed-in session from a `/login` or `/login/complete_link` payload.
 *
 * Called ONLY once the session is really usable: a `MSG_100` carrying
 * `mustSetPassword` is not a session yet, and the login hook holds that state in
 * React until `/password/bootstrap` succeeds. A `mustSetPassword` flag is
 * therefore deliberately NOT stored — a persisted one could only ever be stale,
 * and a stale one would lock somebody out of their own workspace.
 */
export function writeSession(data) {
  const user = data?.user;
  const id = user?._id;
  // A STRING OR A NUMBER, not merely truthy. The API stringifies `_id` on the way
  // out, so anything else is a contract violation — and the one that matters is a
  // raw `{"$oid": "…"}`, which is truthy and which `String()` would store as
  // "[object Object]". That is the worst available outcome: the session looks
  // signed in, and every `/services/*` call answers MSG_401 for the rest of it
  // with nothing on screen that could explain why. Refusing is loud and leaves
  // the user on the sign-in screen, which is recoverable.
  if (!id || (typeof id !== "string" && typeof id !== "number")) {
    console.error("Refusing to write a session with no usable user id", {
      hasUser: Boolean(user),
      idType: typeof id,
    });
    return false;
  }

  writeRaw(STORAGE_KEYS.userId, String(id));
  writeRaw(STORAGE_KEYS.name, user.cName || "");
  writeRaw(STORAGE_KEYS.primaryProvider, data.primaryProvider || "");
  writeList(STORAGE_KEYS.targets, "targets", data.targets);
  writeList(STORAGE_KEYS.providers, "providers", data.providers);

  // Every write cleans up after the build that used to store these.
  LEGACY_KEYS.forEach(removeRaw);

  announce();
  return true;
}

/**
 * What this browser holds. `mustSetPassword` is always false — see
 * `writeSession`; it is in the shape so callers can read the session in one
 * place without special-casing it.
 */
export function readSession() {
  return {
    userId: readRaw(STORAGE_KEYS.userId) || "",
    name: readRaw(STORAGE_KEYS.name) || "",
    primaryProvider: readRaw(STORAGE_KEYS.primaryProvider) || "",
    targets: exportTargets(),
    providers: providerCatalog(),
    mustSetPassword: false,
  };
}

/** Sign out: the session, the cache, and the old build's plaintext leftovers. */
export function clearSession() {
  Object.values(STORAGE_KEYS).forEach(removeRaw);
  LEGACY_KEYS.forEach(removeRaw);
  announce();
}

/**
 * The sites this workspace may offer to export to. `[]` on anything unexpected.
 */
export function exportTargets() {
  return readList(STORAGE_KEYS.targets, "targets");
}

/**
 * The provider descriptors — how to draw a service, what model tasks it accepts,
 * what to say when it is not configured on this deployment. `[]` on anything
 * unexpected, which renders as "no services available" rather than as a row with
 * no name and no accent.
 */
export function providerCatalog() {
  return readList(STORAGE_KEYS.providers, "providers");
}

/**
 * Replace the cache from a server payload — the answer to `/services/connect`,
 * `/services/disconnect`, `/services/list` or `/services/recheck`. Both lists
 * are written together because they are one answer: a target whose descriptor is
 * missing cannot be drawn.
 */
export function writeTargets(targets, providers) {
  writeList(STORAGE_KEYS.targets, "targets", targets);
  writeList(STORAGE_KEYS.providers, "providers", providers);
  announce();
}

/**
 * The export screens' view of the cache. Re-reads on mount, follows same-tab
 * writes, and follows other tabs through the `storage` event — sign out in one
 * tab and the export list in the next empties itself rather than offering a site
 * the session no longer has.
 *
 * It never calls the network. Refreshing from the server needs the actor's
 * password, so it belongs to the screen that can ask for one.
 */
export function useExportTargets() {
  const [state, setState] = useState(() => ({
    targets: exportTargets(),
    providers: providerCatalog(),
  }));

  const reload = useCallback(() => {
    setState({ targets: exportTargets(), providers: providerCatalog() });
  }, []);

  useEffect(() => {
    // A mount after the initial state was computed (a remount, or a write that
    // landed between render and effect) must not show the older list.
    reload();

    // `event.key` is null when a tab called `clear()`; anything else is only
    // interesting when it is one of ours.
    const onStorage = (event) => {
      if (!event || event.key == null) {
        reload();
        return;
      }
      if (event.key === STORAGE_KEYS.targets || event.key === STORAGE_KEYS.providers) {
        reload();
      }
    };

    window.addEventListener(CHANGE_EVENT, reload);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, reload);
      window.removeEventListener("storage", onStorage);
    };
  }, [reload]);

  return { targets: state.targets, providers: state.providers, reload };
}
